import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { createShareLink, generateAIInsights, getClientBundle, refreshAllLfScans, refreshClientLfScan, refreshClientPlaceData, revokeShareLink, syncLfAIScans } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useAuth } from '../lib/auth';
import { ClientDashboard } from './ClientDashboard';
import { NavHeader } from '../components/NavHeader';
import { T, sharedStyles } from '../components/Shared';
import { useIsMobile } from '../lib/useIsMobile';

interface ShareState {
  url: string;
  token: string;
}

export function AdminClientView() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [share, setShare] = useState<ShareState | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const state = useAsync(() => getClientBundle(id!), [id, refreshKey]);

  const handleRefreshPlace = async () => {
    if (!id) return;
    await refreshClientPlaceData(id);
    setRefreshKey(k => k + 1); // re-trigger useAsync to reload bundle with fresh placeData
  };

  const handleRefreshLfAll = async () => {
    if (!id) return;
    // Maps + AI are two distinct LF datasets but conceptually "all scans" from
    // the user's POV. Chain them so a single click syncs both. AI sync runs in
    // auto-discovery mode for new clients (see migration 0013) so admins don't
    // have to maintain a separate keyword list — whatever's in LF appears here.
    await refreshAllLfScans(id);
    try {
      await syncLfAIScans(id);
    } catch (err) {
      // Don't fail the whole refresh if AI sync errors — Maps already landed.
      console.warn('LF AI refresh failed while refreshing heatmap results:', err);
    }
    setRefreshKey(k => k + 1);
  };

  const handleRefreshLfKeyword = async (keyword: string) => {
    if (!id) return;
    await refreshClientLfScan(id, { keyword });
    setRefreshKey(k => k + 1);
  };

  const handleSyncAI = async () => {
    if (!id) return;
    await syncLfAIScans(id);
    setRefreshKey(k => k + 1);
  };

  const handleGenerateInsights = async () => {
    if (!id) return;
    await generateAIInsights(id);
    setRefreshKey(k => k + 1);
  };

  if (state.status === 'loading') {
    return <CenteredMessage title="Loading…" />;
  }
  if (state.status === 'error') {
    return <CenteredMessage title="Failed to load client" detail={state.error.message} />;
  }

  // Client-role users get the read-only chrome (no Share, no AI scan trigger).
  // Team roles (admin/manager/staff) get the full admin experience.
  const isClientRole = user?.role === 'client';

  const handleShare = async () => {
    if (!id) return;
    setShareError(null);
    try {
      const { token, path } = await createShareLink(id);
      setShare({ token, url: `${window.location.origin}${path}` });
    } catch (err) {
      setShareError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: isMobile ? 'auto' : '100vh',
      minHeight: isMobile ? '100vh' : undefined,
      overflow: isMobile ? 'visible' : 'hidden',
    }}>
      {/* Top nav: back to clients + switcher + Sync. Client-role users still
          get the switcher (so multi-location clients can see all their
          branches) but NavHeader itself hides the admin-only bits
          (← All Clients, Sync) when isClientRole is true. */}
      {/* On a phone a client-role session has nothing in this bar but the
          location switcher, and the drawer's "Active business" card is the
          same switcher — so the strip is dropped rather than shown twice. */}
      {!(isMobile && isClientRole) && (
        <NavHeader
          currentClient={state.data.client}
          onReloadBundle={() => setRefreshKey(k => k + 1)}
          isClientRole={isClientRole}
        />
      )}
      <div style={isMobile ? { width: '100%' } : { flex: 1, minHeight: 0 }}>
      <ClientDashboard
        bundle={state.data}
        readOnly={isClientRole}
        onShare={isClientRole ? undefined : handleShare}
        onRefreshPlace={user?.role === 'admin' || user?.role === 'manager' ? handleRefreshPlace : undefined}
        onRefreshLfAll={user?.role === 'admin' || user?.role === 'manager' ? handleRefreshLfAll : undefined}
        onRefreshLfKeyword={user?.role === 'admin' || user?.role === 'manager' ? handleRefreshLfKeyword : undefined}
        onSyncAI={user?.role === 'admin' || user?.role === 'manager' ? handleSyncAI : undefined}
        onGenerateInsights={user?.role === 'admin' || user?.role === 'manager' ? handleGenerateInsights : undefined}
        onReloadBundle={() => setRefreshKey(k => k + 1)}
      />
      </div>
      {share && (
        <ShareModal
          url={share.url}
          onRevoke={async () => {
            await revokeShareLink(share.token);
            setShare(null);
          }}
          onClose={() => setShare(null)}
        />
      )}
      {shareError && (
        <ErrorToast message={shareError} onClose={() => setShareError(null)} />
      )}
    </div>
  );
}

function ErrorToast({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 2100,
      background: '#E8501A', color: '#fff', padding: '12px 16px',
      borderRadius: 8, fontSize: 13, cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)', maxWidth: 360,
    }}>
      <strong>Could not create share link.</strong>
      <div style={{ fontSize: 11, marginTop: 4, fontFamily: "'JetBrains Mono', monospace", opacity: 0.9 }}>{message}</div>
    </div>
  );
}

function CenteredMessage({ title, detail }: { title: string; detail?: string }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 480 }}>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 22, fontWeight: 700, color: T.fgPrimary, marginBottom: 8 }}>
          {title}
        </div>
        {detail && (
          <div style={{ fontSize: 12, color: T.fgMuted, fontFamily: sharedStyles.fontMono, marginTop: 8 }}>{detail}</div>
        )}
      </div>
    </div>
  );
}

function ShareModal({ url, onRevoke, onClose }: { url: string; onRevoke: () => Promise<void>; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const revoke = async () => {
    if (!confirm('Revoke this share link? Anyone holding it will lose access.')) return;
    setRevoking(true);
    try {
      await onRevoke();
    } finally {
      setRevoking(false);
    }
  };
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 28, width: 480, maxWidth: '90vw',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Share Dashboard</div>
        <div style={{ fontSize: 13, color: '#5f6368', marginBottom: 18 }}>
          Anyone with this link can view this client's dashboard. The link is unguessable. You can revoke it at any time.
        </div>
        <div style={{
          display: 'flex', gap: 8, padding: '10px 14px',
          background: '#f8f9fa', border: '1px solid #dadce0', borderRadius: 8, marginBottom: 16,
        }}>
          <input readOnly value={url} style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#202124',
          }} />
          <button onClick={copy} style={{
            background: 'linear-gradient(90deg,#12B5C4,#1E7FD4)', color: '#fff',
            border: 'none', borderRadius: 6, padding: '6px 14px',
            fontWeight: 600, fontSize: 12, cursor: 'pointer',
          }}>{copied ? 'Copied!' : 'Copy'}</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={revoke} disabled={revoking} style={{
            background: 'transparent', border: '1px solid #E8501A', color: '#E8501A',
            borderRadius: 6, padding: '6px 14px', cursor: revoking ? 'wait' : 'pointer',
            fontSize: 13, fontWeight: 600,
            opacity: revoking ? 0.5 : 1,
          }}>{revoking ? 'Revoking…' : 'Revoke link'}</button>
          <button onClick={onClose} style={{
            background: 'transparent', border: '1px solid #dadce0',
            borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13,
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}
